import React, { useState } from 'react';
import { Search, ChevronDown, User } from 'lucide-react';
import { Order } from '../types';
import ClientListSkeleton from './ClientListSkeleton';
import { formatDateLong } from '../utils/dateFormat';

interface ClientSearchProps {
  orders: Order[];
  isLoading: boolean;
  hasSearched: boolean;
  onSearch: (searchTerm: string) => void;
  onOrderSelect: (order: Order) => void;
}

export default function ClientSearch({ orders, isLoading, hasSearched, onSearch, onOrderSelect }: ClientSearchProps) {
  const [searchTerm, setSearchTerm] = useState('');

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const getStatusLabel = (status: string) => {
    const statusMap: Record<string, string> = {
      pedido_realizado: 'Pedido realizado',
      pagamento_aprovado: 'Pagamento aprovado',
      faturamento_realizado: 'Faturamento realizado',
      entrega: 'Entrega',
      cancelado: 'Cancelado'
    };
    return statusMap[status] || status;
  };

  const getStatusDate = (order: Order) => {
    switch (order.status) {
      case 'pagamento_aprovado':
        return order.paymentApprovedAt;
      case 'faturamento_realizado':
        return order.invoicedAt;
      case 'entrega':
        return order.deliveredAt;
      default:
        return order.purchasedAt;
    }
  };

  const handleSearch = () => {
    if (searchTerm.length >= 3) {
      onSearch(searchTerm);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4 md:p-8">
      <div className="mx-auto">
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-10 p-10">
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">
            Buscar Pedidos
          </h1>

          <p className="text-slate-600 mb-8 leading-relaxed">Encontre os pedidos do cliente por CNPJ, Razão Social ou número do pedido.</p>
          <p className="text-slate-600 mb-2 leading-relaxed">- CNPJ (Apenas números)</p>
          <p className="text-slate-600 mb-2 leading-relaxed">- Razão Social (Pode ser apenas um trecho do nome)</p>
          <p className="text-slate-600 mb-8 leading-relaxed">- ID do pedido</p>
            
          <div className="space-y-6">
            <div>
              <label
                htmlFor="client-search"
                className="block text-sm font-semibold text-slate-700 mb-3"
              >
                Procure por cliente específico
              </label>

              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Search
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                    size={20}
                  />
                  <input
                    id="client-search"
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="CNPJ, empresa, nome fantasia"
                    className="w-full pl-12 pr-4 py-3.5 border-2 border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[rgb(37,99,235)] focus:border-transparent transition-all text-slate-700 placeholder:text-slate-400"
                    aria-label="Buscar cliente por CNPJ, empresa ou nome fantasia"
                  />
                </div>

                <button
                  onClick={handleSearch}
                  disabled={searchTerm.length < 3 || isLoading}
                  className="px-8 py-3.5 bg-[rgb(37,99,235)] text-white font-semibold rounded-xl hover:bg-[rgb(29,78,216)] focus:outline-none focus:ring-2 focus:ring-[rgb(37,99,235)] focus:ring-offset-2 disabled:bg-slate-300 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg active:scale-98"
                  aria-label="Buscar pedidos"
                >
                  {isLoading ? 'Buscando...' : 'Buscar Pedidos'}
                </button>
              </div>

              <p className="text-xs text-slate-500 mt-2">
                Use pelo menos 3 caracteres
              </p>
            </div>
          </div>
        </div>

        {isLoading && (
          <div className="mt-8">
            <ClientListSkeleton />
          </div>
        )}

        {!isLoading && hasSearched && orders.length > 0 && (
          <div className="mt-8 space-y-4">
            {orders.map((order) => {
              const statusDate = getStatusDate(order);

              return (
                <div
                  key={order.id}
                  onClick={() => onOrderSelect(order)}
                  className="bg-white rounded-2xl shadow-md hover:shadow-xl transition-all cursor-pointer overflow-hidden group"
                >
                  <div className="flex items-center gap-3 bg-[rgb(29,78,216)] text-white px-6 py-3">
                    <User size={18} />
                    <span className="text-sm font-medium">{order.sellerId ? `Vendido por ${order.sellerId}` : 'Venda Orgânica'}</span>
                  </div>

                  <div className="p-6">
                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                      <div className="border-l-4 border-[rgb(37,99,235)] pl-6">
                        <h3 className="text-sm font-semibold text-slate-500 mb-1">
                          Nome da loja
                        </h3>
                        <p className="text-lg font-bold text-slate-900">
                          {order.storeName}
                        </p>
                      </div>

                      <div>
                        <h3 className="text-sm font-semibold text-slate-500 mb-1">
                          Número do pedido
                        </h3>
                        <p className="text-xl font-bold text-slate-900">
                          {order.orderNumber}
                        </p>
                        <p className="text-sm text-slate-600 mt-2">
                          Valor total
                        </p>
                        <p className="text-lg font-bold text-slate-900">
                          {formatCurrency(order.totalValue)}
                        </p>
                      </div>

                      <div>
                        <h3 className="text-sm font-semibold text-slate-500 mb-1">
                          Forma de pagamento
                        </h3>
                        <p className="text-lg font-semibold text-slate-900">
                          {order.paymentMethod}
                        </p>
                        <p className="text-sm text-slate-600 mt-2">
                          Comprado em
                        </p>
                        <p className="text-sm font-medium text-slate-900">
                          {formatDateLong(order.purchasedAt)}
                        </p>
                      </div>

                      <div>
                        <h3 className="text-sm font-semibold text-slate-500 mb-1">
                          Status do pedido
                        </h3>
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-3 h-3 bg-[rgb(37,99,235)] rounded-full"></div>
                          <span className="text-lg font-semibold text-slate-900">
                            {getStatusLabel(order.status)}
                          </span>
                        </div>
                        {statusDate && (
                          <p className="text-sm text-slate-600">
                            {formatDateLong(statusDate)}
                          </p>
                        )}
                        <button
                          className="mt-2 text-[rgb(37,99,235)] hover:text-[rgb(29,78,216)] flex items-center gap-1 text-sm font-medium transition-colors group-hover:gap-2"
                          aria-label="Ver detalhes do pedido"
                        >
                          <ChevronDown className="transform -rotate-90 transition-all" size={18} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!isLoading && hasSearched && orders.length === 0 && (
          <div className="mt-8 bg-white rounded-xl shadow-md p-8 text-center">
            <p className="text-slate-600 text-lg">
              Nenhum pedido encontrado para "{searchTerm}"
            </p>
            <p className="text-slate-500 text-sm mt-2">
              Tente buscar por outro termo
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
